/**
 * TrialExpiredPlanGrid — the plan chooser on /billing.
 *
 * Why we don't render Clerk's <PricingTable> here:
 *
 * Clerk's PricingTable reads the user's eligibleForFreeTrial flag, which is
 * true for any user with no prior Clerk subscription record. OverlayQA's
 * `teams.beta_expires_at` is invisible to Clerk, so a user who already burned
 * their 14-day OverlayQA trial still appears as "never trialed" to Clerk —
 * the PricingTable shows "Start free trial" buttons on Starter and
 * Team, letting users stack a second trial.
 *
 * This component renders our own cards with `<CheckoutButton>` from Clerk's
 * experimental package. The visible CTA is "Subscribe" (no trial language).
 * Clerk's checkout drawer still does its own thing internally, but the user
 * never sees the trial offer in our UI.
 *
 * Prices and features are fetched from the server's /api/billing/plans
 * endpoint, which proxies clerk.billing.getPlanList(). Source of truth: the
 * Clerk dashboard. Never hardcode prices in this component — they drift.
 *
 * Mounted in BillingPage for any owner without an active plan, and for
 * owners whose plan comes from an active beta (`betaExpiresAt` in the
 * future) — both groups look "never subscribed" to Clerk and would be
 * offered a fresh free trial by the PricingTable.
 *
 * ---------------------------------------------------------------------------
 * THE PRICING CARDS TEMPLATE (Emily, 2026-10-25: "all pricing cards sections should be templates ... look at the
 * billing one, add the per seat addition we have there", then "go with recos" on porting it). This grid renders the
 * same card as overlayqa.com's pricing (OverlayQALaunch src/components/sections/PricingPlans.tsx and the template page
 * prototypes/pricing-cards-template.html there): toggle, fit line, price, billing line, dollar saving, seats, the seat
 * chooser, the button, and every feature in every card, with the same classes (./pricing-cards.css, a copy of that
 * repo's stylesheet with container queries) and strings (./pricingCard.ts, a copy of its data). That repo's
 * scripts/pricing-cards-data.mjs fails its build when either copy drifts, and asserts the billing strings below against
 * this file and SubscriptionSeats.tsx. What stays billing's own: prices from Clerk, the Stripe and Clerk checkouts, the
 * intent labels, "Billing starts today.", the sales address under Contact sales, the plan badges and the brand-blue
 * outline on a current plan that is not the recommended one (Emily, 2026-09-01).
 *
 * Three things on the marketing card are FALSE once someone is signed in and standing at a checkout that takes a
 * card, so they are not ported: "No credit card required. Cancel anytime.", the 14-day-trial reassurance row, and the
 * trial button itself. Pinned as a control in TrialExpiredPlanGrid.pricingParity.test.tsx.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, MutableRefObject } from 'react';
import { CheckoutButton } from '@clerk/clerk-react/experimental';
import { SALES_ASSISTED_PLAN_SLUGS, getSeatLimit, planAllowsExtraSeats, EXTRA_SEAT_MONTHLY_PRICE_USD, MAX_EXTRA_SEATS } from '@overlayqa/shared';
import { useStripeRedirect } from '../../hooks/useStripeRedirect';
import { stripeRedirectErrorCopy } from '../../lib/stripeRedirect';
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowDown01Icon, StarIcon, Tick02Icon, UserMultipleIcon } from '@hugeicons/core-free-icons';
import type { BillingPlanInfo } from '@overlayqa/shared';
import { cn } from '@/lib/utils';
import { useBillingPlans } from '../../hooks/useBillingPlans';
import { useBillingRefresh } from '../../hooks/useBillingRefresh';
import { capture } from '../../lib/analytics';
import { LoadError } from '../shared/IssuesLoadError';
import { Button, buttonVariants } from '../ui/button';
import { SubscriptionSeats, billingMoney, fillCopy, type SubscriptionSeatsProps } from './SubscriptionSeats';
import { PRICING_CARD } from './pricingCard';
import { BillingPlansSkeleton } from './BillingSkeleton';
import { useBillingSeatSelection } from '../../hooks/useBillingSeatSelection';
import './BillingChoices.css';
import './pricing-cards.css';

const COPY = PRICING_CARD.copy;
const PLAN_LINES = PRICING_CARD.plans as Record<string, { fit: string; extras: readonly string[] }>;

// Plan ordering for display. Lower number = leftmost card. Slugs that aren't
// in this map appear after the known ones in their natural order.
const PLAN_DISPLAY_ORDER: Record<string, number> = {
  starter: 0,
  team: 1,
  pro: 2,
};

// Which plan wears the gradient border and the "Most Popular" badge. Matches
// the marketing grid's highlighted tier and Clerk's PricingTable default.
const HIGHLIGHTED_SLUG = 'team';

/**
 * Sales-assisted plans get no self-serve checkout (Emily, 2026-09-14: "pro
 * shouldnt have a self sign up"). The marketing card has carried
 * `cta: 'Contact sales'` on Pro since 2026-04-11 (OverlayQALaunch
 * src/data/pricing.ts) while this grid offered a Clerk checkout on it; the
 * two now agree. Address and subject are that site's strings verbatim
 * (ContactSalesDialog.tsx SALES_EMAIL; Pricing.tsx subject="Pro Plan Inquiry").
 * The address is also printed under the CTA: a bare mailto silently does
 * nothing on a machine with no mail client, which is why the marketing site
 * opens a dialog, and a visible address is the cheaper fallback here.
 */
// ONE list, shared with the server, which refuses a checkout for these slugs:
// hiding the button must never be the only thing between a request and a Pro checkout.
const SALES_ASSISTED_SLUGS = SALES_ASSISTED_PLAN_SLUGS;
const SALES_EMAIL = 'hello@overlayqa.com';
const SALES_MAILTO = `mailto:${SALES_EMAIL}?subject=${encodeURIComponent('Pro Plan Inquiry')}`;

/**
 * Which price the user is looking at, and therefore which price we charge.
 * Clerk's own union (`BillingSubscriptionPlanPeriod`); passed to CheckoutButton.
 */
type BillingPeriod = 'month' | 'annual';

/**
 * Resolved PER PLAN, not per grid. A plan Clerk has no annual price for stays
 * monthly even while the toggle says Annual, because the alternative is
 * showing a monthly number under an "Annual" heading. Pairing the period with
 * the fee in one function is what keeps the price on screen and the period we
 * bill from drifting apart.
 */
function periodFor(plan: BillingPlanInfo, isAnnual: boolean): BillingPeriod {
  return isAnnual && plan.annualMonthlyFee ? 'annual' : 'month';
}

function feeFor(plan: BillingPlanInfo, period: BillingPeriod): string {
  return period === 'annual' && plan.annualMonthlyFee ? plan.annualMonthlyFee : plan.monthlyFee;
}

function comparePlans(a: BillingPlanInfo, b: BillingPlanInfo): number {
  const orderA = PLAN_DISPLAY_ORDER[a.slug] ?? 99;
  const orderB = PLAN_DISPLAY_ORDER[b.slug] ?? 99;
  return orderA - orderB;
}

/**
 * What the CTA on a given card is actually offering, relative to where the
 * user already is. `subscribe` is the original expired-user case.
 */
type PlanIntent = 'subscribe' | 'keep' | 'current' | 'upgrade' | 'switch';

function planIntent(
  plan: BillingPlanInfo,
  current: BillingPlanInfo | undefined,
  isTrialing: boolean,
): PlanIntent {
  if (!current) return 'subscribe';
  if (plan.slug === current.slug) return isTrialing ? 'keep' : 'current';
  // Ranked on PRICE, not on PLAN_DISPLAY_ORDER: that map is a layout
  // preference and would mislabel a re-ordered grid. Prices come from Clerk
  // via the server, so this follows what we actually charge.
  //
  // Ranked on the MONTHLY fee specifically, and deliberately not on whichever
  // period the toggle is showing: "is Pro dearer than Team" is a fact about
  // the plans, not about how you pay for them, and monthlyFee is the one fee
  // every Clerk plan is guaranteed to have (annualMonthlyFee is nullable).
  const dearer = parseFloat(plan.monthlyFee) > parseFloat(current.monthlyFee);
  return dearer ? 'upgrade' : 'switch';
}

function ctaLabel(intent: PlanIntent, planName: string): string {
  switch (intent) {
    // Mid-trial the viewer already has this plan and this button is what stops
    // them losing it. From 2026-09-16 it read "Keep ${planName}", because
    // "Subscribe to Team" had read as an offer to buy what they were already
    // using; then a real trialing owner read "keep" as a state he already held
    // and asked whether any action was needed, with no payment anywhere
    // (PixelParade, 2026-10-05). The click opens a payment form whose own
    // button says "Subscribe" and this grid is labelled "Subscribe to a plan",
    // so the label names that act; the "Your trial plan" badge carries "you
    // are on this one" (Emily, 2026-10-06: "yes change to Subscribe"). The
    // intent stays distinct for analytics (`intent: 'keep'`) and the badge.
    case 'keep': return `Subscribe to ${planName}`;
    case 'current': return `Your current plan`;
    case 'upgrade': return `Upgrade to ${planName}`;
    case 'switch': return `Switch to ${planName}`;
    default: return `Subscribe to ${planName}`;
  }
}

interface TrialExpiredPlanGridProps {
  /** Slug of the plan the viewer is on now, or null when they have none. */
  currentPlanSlug?: string | null;
  /** True while that plan comes from an active trial rather than a payment. */
  isTrialing?: boolean;
  /**
   * Reports a decision gesture up to BillingPage, so `billing_page_exited` can
   * say whether the reader evaluated anything before leaving. Separate from
   * the events fired here: those count gestures, this one lets the EXIT event
   * describe a visit that ended without one.
   */
  onInteraction?: (kind: 'toggle' | 'cta') => void;
  /**
   * Which checkout a plan card opens: Stripe's hosted Checkout page, or
   * Clerk's in-app drawer. From TeamBillingInfo.checkoutProvider; absent reads
   * as 'clerk', the pre-2026-09-18 flow.
   */
  checkoutProvider?: 'stripe' | 'clerk';
  checkoutSeatsAvailable?: boolean;
  currentSeats?: number;
  selectionKey?: string;
  /**
   * Clerk drawer completion. The billing page passes its activation flow
   * (pending card, then the success message); absent, the grid just refreshes.
   */
  onSubscriptionComplete?: () => void;
}

export function TrialExpiredPlanGrid({
  currentPlanSlug = null,
  isTrialing = false,
  onInteraction,
  checkoutProvider = 'clerk',
  checkoutSeatsAvailable = false,
  currentSeats = 1,
  selectionKey,
  onSubscriptionComplete,
}: TrialExpiredPlanGridProps = {}) {
  const { data: plans, isLoading, error, refetch } = useBillingPlans();
  const refreshBilling = useBillingRefresh();
  const stripeRedirect = useStripeRedirect();

  // Opens on Monthly, matching the marketing grid and the deliberate
  // monthly-default this app already forces on Clerk's own PricingTable
  // (see lib/useForceMonthlyDefault.ts).
  const selection = useBillingSeatSelection(selectionKey);
  const isAnnual = selection.annual;
  const setIsAnnual = selection.setAnnual;
  // Prices roll into place only once the reader has switched the period (never on first paint).
  const [switched, setSwitched] = useState(false);
  // Phones: which folded feature lists the reader opened.
  const [openFeatures, setOpenFeatures] = useState<Record<string, boolean>>({});
  const periodOpts = useRef<Array<HTMLButtonElement | null>>([]);

  const ordered = useMemo(() => {
    if (!plans) return [];
    return [...plans].sort(comparePlans);
  }, [plans]);

  // No annual prices configured in Clerk means the toggle would be a control
  // that changes nothing, so it is not rendered at all.
  const hasAnnualPricing = useMemo(
    () => ordered.some((p) => Boolean(p.annualMonthlyFee)),
    [ordered],
  );

  /** "Save up to N%", floored so "up to" stays true; only plans Clerk prices both ways count. */
  const maxSave = useMemo(() => {
    const cuts = ordered
      .filter((p) => p.annualMonthlyFee)
      .map((p) => Math.floor((1 - parseFloat(p.annualMonthlyFee as string) / parseFloat(p.monthlyFee)) * 100));
    return cuts.length ? Math.max(0, ...cuts) : 0;
  }, [ordered]);

  const currentPlan = useMemo(
    () => ordered.find((p) => p.slug === currentPlanSlug),
    [ordered, currentPlanSlug],
  );

  /**
   * The toggle shipped 2026-09-01 as a real control -- it re-resolves every
   * card's price AND the period the checkout opens on -- and emitted nothing,
   * so "did this reader evaluate the price" was unanswerable. Guarded on an
   * actual change: re-clicking the selected period is not an evaluation, and
   * counting it would inflate the one signal this exists to measure.
   */
  const handlePeriodChange = useCallback(
    (annual: boolean, focus = false) => {
      if (focus) periodOpts.current[annual ? 1 : 0]?.focus();
      if (annual === isAnnual) return;
      setIsAnnual(annual);
      setSwitched(true);
      capture('billing_plan_period_toggled', {
        to_period: annual ? 'annual' : 'month',
        current_plan_slug: currentPlanSlug,
      });
      onInteraction?.('toggle');
    },
    [isAnnual, currentPlanSlug, onInteraction, setIsAnnual],
  );

  const handleClick = useCallback(
    (slug: string, intent: PlanIntent, period: BillingPeriod) => {
      // `current_plan_slug` + `intent` make the click readable: an upgrade, a
      // downgrade and a trial conversion were previously one undifferentiated
      // event, so the grid could not say which decision users were making.
      // `billing_period` is what the CHECKOUT was opened for, resolved per
      // plan, so it never claims annual for a plan that has no annual price.
      // `sales_assisted` says whether this click opened mail to sales rather
      // than a Clerk checkout, so a Pro click is never read as a checkout open.
      capture('trial_expired_grid_cta_clicked', {
        plan_slug: slug,
        current_plan_slug: currentPlanSlug,
        intent,
        billing_period: period,
        sales_assisted: SALES_ASSISTED_SLUGS.has(slug),
        // Which checkout this click opens, so the Stripe and Clerk funnels can
        // be read apart across the release-flag flip.
        checkout_provider: checkoutProvider,
      });
      onInteraction?.('cta');
    },
    [currentPlanSlug, onInteraction, checkoutProvider],
  );

  if (isLoading) {
    return <BillingPlansSkeleton />;
  }

  if (error || ordered.length === 0) {
    return (
      <div className="card-sm">
        <LoadError
          compact
          resource="plans"
          error={error instanceof Error ? error : new Error('No plans were returned')}
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  return (
    <section className="billing-choice-surface" aria-label="Subscribe to a plan">
      <div className="pp" data-context="billing" data-billing={isAnnual ? 'annual' : 'monthly'}>
        {hasAnnualPricing && (
          <BillingPeriodToggle isAnnual={isAnnual} maxSave={maxSave} optRefs={periodOpts} onChange={handlePeriodChange} />
        )}

        {stripeRedirect.errorCode && (
          <p role="alert" className="mb-2 text-sm text-red-600">
            {stripeRedirectErrorCopy(stripeRedirect.errorCode)}
          </p>
        )}

        <div className="pp-cards" data-roll={switched || undefined}>
          {ordered.map((plan, index) => {
            const intent = planIntent(plan, currentPlan, isTrialing);
            const period = periodFor(plan, isAnnual);
            const included = getSeatLimit(plan.slug);
            const supportsSeats = checkoutProvider === 'stripe' && checkoutSeatsAvailable && planAllowsExtraSeats(plan.slug) && plan.currency === 'USD';
            const total = Math.max(included, currentSeats, Math.min(included + MAX_EXTRA_SEATS, selection.totals[plan.slug] ?? included));
            const seatChoice: SubscriptionSeatsProps | undefined = supportsSeats ? {
              slug: plan.slug, name: plan.name, included, members: currentSeats, total,
              base: Number(feeFor(plan, period)), period,
              disabled: stripeRedirect.pendingKey !== null || currentSeats > included + MAX_EXTRA_SEATS,
              onChange: value => {
                selection.setTotal(plan.slug, value);
                capture('subscription_seats_changed', { plan_slug: plan.slug, total_seats: value, extra_seats: value - included });
                onInteraction?.('toggle');
              },
            } : undefined;
            return (
              <PlanCard
                key={plan.id}
                plan={plan}
                index={index}
                highlighted={plan.slug === HIGHLIGHTED_SLUG}
                period={period}
                intent={intent}
                seatChoice={seatChoice}
                featuresOpen={Boolean(openFeatures[plan.slug])}
                onToggleFeatures={() => {
                  const next = !openFeatures[plan.slug];
                  setOpenFeatures((prev) => ({ ...prev, [plan.slug]: next }));
                  capture('billing_features_toggled', { plan_slug: plan.slug, expanded: next });
                }}
                onSaveClick={() => {
                  capture('billing_annual_saving_clicked', { plan_slug: plan.slug });
                  handlePeriodChange(true, true);
                }}
                onClick={() => handleClick(plan.slug, intent, period)}
                onSubscriptionComplete={onSubscriptionComplete ?? refreshBilling}
                stripe={
                  checkoutProvider === 'stripe'
                    ? {
                        pendingKey: stripeRedirect.pendingKey,
                        open: () => void stripeRedirect.open({ kind: 'checkout', planSlug: plan.slug, period, ...(supportsSeats ? { extraSeats: total - included } : {}) }),
                      }
                    : null
                }
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}

/**
 * Monthly/Annual switch: the template's radiogroup (one option checked, arrow
 * keys move between them) with a blue thumb that slides to the chosen period,
 * and "Save up to N%" from Clerk's own fees.
 */
function BillingPeriodToggle({
  isAnnual,
  maxSave,
  optRefs,
  onChange,
}: {
  isAnnual: boolean;
  maxSave: number;
  optRefs: MutableRefObject<Array<HTMLButtonElement | null>>;
  onChange: (annual: boolean, focus?: boolean) => void;
}) {
  const toggleRef = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);

  const place = useCallback((): boolean => {
    const g = toggleRef.current;
    const active = optRefs.current[isAnnual ? 1 : 0];
    if (!g || !active || !active.offsetWidth) return false;
    g.style.setProperty('--pp-x', `${active.offsetLeft}px`);
    g.style.setProperty('--pp-w', `${active.offsetWidth}px`);
    return true;
  }, [isAnnual, optRefs]);

  useLayoutEffect(() => { place(); }, [place]);
  useEffect(() => {
    const g = toggleRef.current;
    if (!g || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => { if (place()) requestAnimationFrame(() => setReady(true)); });
    ro.observe(g);
    return () => ro.disconnect();
  }, [place]);

  const onKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (!['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(e.key)) return;
    e.preventDefault();
    onChange(!isAnnual, true);
  };

  return (
    <div className="pp-toggle-row">
      <div ref={toggleRef} className={cn('pp-toggle', ready && 'is-ready')} role="radiogroup" aria-label={COPY.toggle.label}>
        <span className="pp-thumb" aria-hidden="true" />
        {([false, true] as const).map((annual, i) => (
          <button
            key={annual ? 'annual' : 'monthly'}
            ref={(el) => { optRefs.current[i] = el; }}
            type="button"
            className="pp-opt"
            role="radio"
            aria-checked={annual === isAnnual}
            tabIndex={annual === isAnnual ? 0 : -1}
            onClick={() => onChange(annual)}
            onKeyDown={onKey}
          >
            {annual ? COPY.toggle.annual : COPY.toggle.monthly}
            {annual && maxSave > 0 && <span className="pp-chip">{fillCopy(COPY.toggle.chip, { pct: maxSave })}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

interface PlanCardProps {
  seatChoice?: SubscriptionSeatsProps;
  plan: BillingPlanInfo;
  /** Position in the grid: after the first card, the repeated feature list folds on narrow layouts. */
  index: number;
  highlighted: boolean;
  /** The period this card is quoting AND the one its checkout will open on. */
  period: BillingPeriod;
  intent: PlanIntent;
  featuresOpen: boolean;
  onToggleFeatures: () => void;
  onSaveClick: () => void;
  onClick: () => void;
  /** Fired by Clerk when the checkout completes — used to clear the paywall. */
  onSubscriptionComplete: () => void;
  /**
   * Set when checkout is Stripe's hosted page: the CTA then leaves for Stripe
   * instead of opening Clerk's drawer. `pendingKey` is the slug whose redirect
   * is in flight, or 'portal', or null.
   */
  stripe: { pendingKey: string | null; open: () => void } | null;
}

const icon = (glyph: typeof Tick02Icon) => (
  <HugeiconsIcon icon={glyph} size={16} strokeWidth={2} color="currentColor" className="pp-ic" aria-hidden="true" />
);

function PlanCard({
  plan,
  index,
  highlighted,
  period,
  intent,
  featuresOpen,
  onToggleFeatures,
  onSaveClick,
  onClick,
  onSubscriptionComplete,
  stripe,
  seatChoice,
}: PlanCardProps) {
  const lines = PLAN_LINES[plan.slug];
  const base = seatChoice ? seatChoice.base : Number(feeFor(plan, period));
  const monthly = base + (seatChoice ? (seatChoice.total - seatChoice.included) * EXTRA_SEAT_MONTHLY_PRICE_USD : 0);
  // Trim trailing ".00" for whole-dollar prices to match Clerk's PricingTable
  // formatting ("$39/mo" not "$39.00/mo"). Keep decimals when present.
  const fee = seatChoice ? monthly.toLocaleString('en-US', { maximumFractionDigits: 2 }) : feeFor(plan, period).replace(/\.00$/, '');
  const currencySymbol = plan.currency === 'USD' ? '$' : '';
  const isCurrent = intent === 'keep' || intent === 'current';
  const label = seatChoice && intent !== 'current' ? `Continue with ${plan.name}` : ctaLabel(intent, plan.name);
  const saving = plan.annualMonthlyFee ? (parseFloat(plan.monthlyFee) - parseFloat(plan.annualMonthlyFee)) * 12 : 0;
  const included = getSeatLimit(plan.slug);

  return (
    <article
      // ONE edge treatment per card, always, and which one it gets says why the
      // eye should go there:
      //   recommended -> the marketing gradient border (this is the tier we
      //                  push, painted exactly as overlayqa.com/pricing paints
      //                  it);
      //   yours       -> a brand-blue outline (Emily, 2026-09-01: "make current
      //                  plan outline blue not that green and our brand blue").
      // The common case is one card being BOTH: measured 2026-08-31, 14 of 17
      // billing views are mid-trial on Team, which is also the recommended
      // tier. There the gradient wins and the BADGE carries "yours", because
      // stacking a blue outline outside a gradient border reads as a bug.
      className={cn(
        'pp-card',
        highlighted && 'pp-card--featured card-shimmer-border',
        !highlighted && isCurrent && 'pp-card--current',
      )}
      data-plan={plan.slug}
      aria-labelledby={`pp-name-${plan.slug}`}
    >
      <header className="pp-head">
        <h3 id={`pp-name-${plan.slug}`} className="pp-name">{plan.name}</h3>
        {isCurrent ? (
          <span className="pp-chip pp-chip--plan">{intent === 'keep' ? COPY.billing.trialBadge : 'Current plan'}</span>
        ) : highlighted ? (
          <span className="badge-gradient-bg inline-flex items-center gap-2 whitespace-nowrap rounded-full px-2 py-1 text-xs font-semibold text-white shadow-lg">
            <HugeiconsIcon icon={StarIcon} size={12} strokeWidth={2} color="currentColor" className="shrink-0" aria-hidden="true" />
            Most Popular
          </span>
        ) : null}
      </header>
      {/* Always rendered, so the slot keeps its row and the prices stay level across the grid. */}
      <p className="pp-fit">{lines?.fit ?? plan.description ?? ''}</p>

      <p className="pp-price" data-plan-price>
        <span key={period} className="pp-amount pp-val">{currencySymbol}{fee}</span>
        <span className="pp-per">/mo</span>
      </p>
      <p className="pp-bill">
        <span key={period} className="pp-val">
          {period === 'annual'
            ? fillCopy(COPY.billing.yearlyTotal, { amount: billingMoney(monthly * 12) })
            : fillCopy(COPY.billing.monthlyTotal, { amount: billingMoney(monthly) })}
        </span>
      </p>
      <p className="pp-save">
        {saving <= 0 ? null : period === 'annual' ? (
          <span key="annual" className="pp-val">{fillCopy(COPY.save.annual, { amount: billingMoney(saving) })}</span>
        ) : (
          <button key="monthly" type="button" className="pp-save-link pp-val" onClick={onSaveClick}>
            {fillCopy(COPY.save.monthly, { amount: billingMoney(saving) })}
          </button>
        )}
      </p>

      <div className="pp-seats">
        <ul className="pp-seat-list">
          <li className="pp-seat-main">{icon(UserMultipleIcon)}<span>{included} {included === 1 ? 'seat' : 'seats'} {COPY.seats.included}</span></li>
          {seatChoice && (
            <li className="pp-seat-price"><span>{fillCopy(COPY.seats.extraPrice, { price: billingMoney(EXTRA_SEAT_MONTHLY_PRICE_USD) })}</span></li>
          )}
          {(lines?.extras ?? []).map((extra) => <li key={extra}>{icon(Tick02Icon)}<span>{extra}</span></li>)}
        </ul>
        {seatChoice && <SubscriptionSeats {...seatChoice} />}
      </div>

      <div className="pp-cta">
        {intent === 'current' ? (
          // Already paying for this one. Nothing to sell, and a live checkout
          // button here would offer to buy a plan they hold — the same
          // incoherence this change exists to remove.
          <Button
            type="button"
            variant={highlighted ? 'primary' : 'outline'}
            className="w-full"
            disabled
          >
            {label}
          </Button>
        ) : SALES_ASSISTED_SLUGS.has(plan.slug) ? (
          <>
            {/* An <a> styled as the CTA, never a <button> inside an <a>: the
                marketing dialog's "Open email app" is the same shape. */}
            <a
              href={SALES_MAILTO}
              onClick={onClick}
              className={cn(buttonVariants({ variant: highlighted ? 'primary' : 'outline' }), 'w-full')}
            >
              <span className="text-cap-trim">Contact sales</span>
            </a>
            <p className="pp-cta-note">{SALES_EMAIL}</p>
          </>
        ) : stripe ? (
          <>
            {/* Stripe's hosted Checkout: the whole tab leaves for Stripe's own
                page (Emily, 2026-09-18). Same label, same hierarchy as the Clerk
                CTA below. EVERY buy button closes while one redirect is in
                flight: a press on a second card would open a second session. */}
            <Button
              type="button"
              variant={highlighted ? 'primary' : 'outline'}
              className="w-full"
              isLoading={stripe.pendingKey === plan.slug}
              aria-busy={stripe.pendingKey === plan.slug}
              disabled={stripe.pendingKey !== null || Boolean(seatChoice && seatChoice.members > seatChoice.included + MAX_EXTRA_SEATS)}
              onClick={() => {
                onClick();
                stripe.open();
              }}
            >
              {label}
            </Button>
            {/* Still true on Stripe: a subscription with no trial is charged at
                confirmation. */}
            <p className="pp-cta-note">{COPY.billing.startsToday}</p>
          </>
        ) : (
          <>
            <CheckoutButton
              planId={plan.id}
              planPeriod={period}
              for="user"
              onSubscriptionComplete={onSubscriptionComplete}
            >
              {/* ui/Button, not `.btn-primary`: this is a conversion CTA (44px), not
                  app chrome (36px). See buttonSystemParity.source.test.ts.
                  Filled on the recommended tier, outline elsewhere — the same
                  hierarchy the marketing grid uses. */}
              <Button
                type="button"
                variant={highlighted ? 'primary' : 'outline'}
                onClick={onClick}
                className="w-full"
              >
                {label}
              </Button>
            </CheckoutButton>
            {/* Said before the click, not discovered inside Clerk's drawer: a plan
                with no free trial is charged at confirmation ("Total Due Today" on
                Clerk's own <CheckoutButton> demo), and these plans have carried no
                trial since 2026-07-04 (Emily, 2026-09-16). Same class as the
                address under Contact sales. */}
            <p className="pp-cta-note">{COPY.billing.startsToday}</p>
          </>
        )}
      </div>

      <div className="pp-features-wrap">
        {index > 0 && (
          <button type="button" className="pp-more" aria-expanded={featuresOpen} aria-controls={`pp-feat-${plan.slug}`} onClick={onToggleFeatures}>
            <span>{featuresOpen ? COPY.features.hide : fillCopy(COPY.features.show, { n: PRICING_CARD.features.length })}</span>
            {icon(ArrowDown01Icon)}
          </button>
        )}
        <ul className="pp-features" id={`pp-feat-${plan.slug}`} data-collapsed={index > 0 && !featuresOpen ? '' : undefined}>
          {PRICING_CARD.features.map((feature) => <li key={feature}>{icon(Tick02Icon)}<span>{feature}</span></li>)}
        </ul>
      </div>
    </article>
  );
}
